import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper, 
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { 
  ContactList as ListType,
  getContactLists, 
  createContactList, 
  deleteContactList,
  addContactsToList,
  createSmartList,
  getSmartLists,
  deleteSmartList
} from '../../firebase/services/listService';
import { getContacts, Contact } from '../../firebase/services/contactService';
import { useNavigate } from 'react-router-dom';
import type { SmartList } from '../../firebase/services/listService';

/**
 * Component for managing contact lists
 */
const ListManager = () => {
  const navigate = useNavigate();
  // State for lists and UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [contactLists, setContactLists] = useState<ListType[]>([]);
  const [smartLists, setSmartLists] = useState<SmartList[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  
  // State for dialogs
  const [newListDialogOpen, setNewListDialogOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState<{
    open: boolean;
    listId: string;
    listName: string;
  }>({
    open: false,
    listId: '',
    listName: ''
  });
  
  const [selectedList, setSelectedList] = useState<ListType | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [addContactDialogOpen, setAddContactDialogOpen] = useState(false);
  
  // Add state for search, filter, and selected contacts
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  
  // Smart list dialog
  const [smartListDialogOpen, setSmartListDialogOpen] = useState(false);
  const [smartListName, setSmartListName] = useState('');
  const [smartListTag, setSmartListTag] = useState('');
  const [smartListMinTransactions, setSmartListMinTransactions] = useState('');
  
  // Filtered and searched contacts
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch =
      contact.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTag = filterTag ? contact.tags?.includes(filterTag) : true;
    return matchesSearch && matchesTag;
  });
  
  // Compute smart list preview
  const smartListPreview = contacts.filter(contact => {
    const matchesTag = smartListTag ? contact.tags?.includes(smartListTag) : true;
    // Placeholder: Assume contact.transactions is a number (add this to Contact type if needed)
    const matchesTransactions = smartListMinTransactions ? (contact.transactions || 0) >= parseInt(smartListMinTransactions) : true;
    return matchesTag && matchesTransactions;
  });
  
  const handleToggleContact = (id: string) => {
    setSelectedContactIds(prev =>
      prev.includes(id) ? prev.filter(cid => cid !== id) : [...prev, id]
    );
  };
  
  // Load lists data
  const loadLists = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const lists = await getContactLists({
        orderBy: 'name',
        orderDirection: 'asc'
      });
      setContactLists(lists);
    } catch (err) {
      setError('Failed to load contact lists');
      console.error('Error loading lists:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch smart lists
  const loadSmartLists = async () => {
    try {
      const lists = await getSmartLists();
      setSmartLists(lists);
    } catch (err) {
      setError('Failed to load smart lists');
      console.error('Error loading smart lists:', err);
    }
  };
  
  // Load lists on mount
  useEffect(() => {
    loadLists();
    loadSmartLists();
    fetchContacts();
  }, []);
  
  const fetchContacts = async () => {
    try {
      const { contacts: contactsData } = await getContacts();
      setContacts(contactsData);
    } catch (err) {
      console.error('Error loading contacts:', err);
    }
  };
  
  // Handle creating a new list
  const handleCreateList = async () => {
    if (newListName.trim()) {
      try {
        setNewListDialogOpen(false);
        setLoading(true);
        
        // Create new list in Firebase
        const newList = await createContactList({
          name: newListName.trim(),
          description: newListDescription.trim(),
          tags: []
        });
        
        // Show success message
        setSuccess(`List "${newListName}" created successfully`);
        
        // Reload lists to show the new one
        await loadLists();
        
        // Reset form values
        setNewListName('');
        setNewListDescription('');
      } catch (err) {
        setError(`Failed to create list: ${err instanceof Error ? err.message : String(err)}`);
        console.error('Error creating list:', err);
        setLoading(false);
      }
    }
  };
  
  // Handle deleting a list
  const handleDeleteList = async () => {
    const { listId, listName } = confirmDeleteDialog;
    
    try {
      setConfirmDeleteDialog({ open: false, listId: '', listName: '' });
      setLoading(true);
      
      // Delete list in Firebase
      await deleteContactList(listId);
      
      // Show success message
      setSuccess(`List "${listName}" deleted successfully`);
      
      // Update local state
      setContactLists(prev => prev.filter(list => list.id !== listId));
      setLoading(false);
    } catch (err) {
      setError(`Failed to delete list: ${err instanceof Error ? err.message : String(err)}`);
      console.error('Error deleting list:', err);
      setLoading(false);
    }
  };
  
  // Confirm delete dialog
  const confirmDelete = (list: ListType) => {
    setConfirmDeleteDialog({
      open: true,
      listId: list.id,
      listName: list.name
    });
  };
  
  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  const handleAddContactsToList = async () => {
    if (!selectedList || selectedContactIds.length === 0) return;
    try {
      await addContactsToList(selectedList.id, selectedContactIds);
      await loadLists();
      setAddContactDialogOpen(false);
      setSelectedContactIds([]);
    } catch (err) {
      setError('Failed to add contacts to list');
      console.error('Error adding contacts to list:', err);
    }
  };
  
  // Handle deleting a smart list
  const handleDeleteSmartList = async (id: string, name: string) => {
    try {
      await deleteSmartList(id);
      setSmartLists(prev => prev.filter(list => list.id !== id));
      setSuccess(`Smart list "${name}" deleted successfully`);
    } catch (err) {
      setError('Failed to delete smart list');
      console.error('Error deleting smart list:', err);
    }
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Contact Lists
        </Typography>
        <Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={() => setNewListDialogOpen(true)}
            sx={{ mr: 2 }}
          >
            Create List
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setSmartListDialogOpen(true)}
          >
            Create Smart List
          </Button>
        </Box>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid xs={12} md={8}>
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
              </Box>
            ) : contactLists.length === 0 && smartLists.length === 0 ? (
              <Box p={4} textAlign="center">
                <Typography variant="body1" color="textSecondary">
                  No contact lists found. Click "Create List" to get started.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Contacts</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Regular lists */}
                    {contactLists.map((list) => (
                      <TableRow key={list.id}>
                        <TableCell>{list.name}</TableCell>
                        <TableCell>{list.description || `${list.contactCount} contacts`}</TableCell>
                        <TableCell>{list.contactCount}</TableCell>
                        <TableCell>
                          <IconButton edge="end" aria-label="add contacts" onClick={() => {
                            setSelectedList(list);
                            setAddContactDialogOpen(true);
                          }}>
                            <PersonAddIcon />
                          </IconButton>
                          <IconButton edge="end" aria-label="edit" onClick={() => navigate(`/admin/business/marketing/lists/${list.id}`)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => confirmDelete(list)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* Smart lists */}
                    {smartLists.map((list) => (
                      <TableRow key={list.id} sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell>
                          {list.name} <Chip label="Smart" size="small" color="secondary" sx={{ ml: 1 }} />
                        </TableCell>
                        <TableCell>
                          {list.rules.tag ? `Tag: ${list.rules.tag}` : ''}
                          {list.rules.minTransactions ? `Min Transactions: ${list.rules.minTransactions}` : ''}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>
                          {/* No add contacts for smart lists */}
                          <IconButton edge="end" aria-label="edit" disabled>
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            edge="end" 
                            aria-label="delete"
                            onClick={() => handleDeleteSmartList(list.id, list.name)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
        
        <Grid xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              List Management Tips
            </Typography>
            <Typography variant="body2" paragraph>
              Contact lists help you organize your audience and send targeted campaigns.
            </Typography>
            <Typography variant="body2" paragraph>
              Segmenting your audience into lists can significantly increase your email engagement rates.
            </Typography>
            <Typography variant="body2">
              Best practices:
            </Typography>
            <ul>
              <li>
                <Typography variant="body2">
                  Create lists based on customer interests
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Regularly clean your lists of inactive subscribers
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Use descriptive names for your lists
                </Typography>
              </li>
            </ul>
          </Paper>
        </Grid>
      </Grid>
      {/* New List Dialog */}
      <Dialog open={newListDialogOpen} onClose={() => setNewListDialogOpen(false)}>
        <DialogTitle>Create New Contact List</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enter details for your new contact list. You can add contacts to it later.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            fullWidth
            variant="outlined"
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            value={newListDescription}
            onChange={(e) => setNewListDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewListDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateList} variant="contained" color="primary" disabled={!newListName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Delete Dialog */}
      <Dialog
        open={confirmDeleteDialog.open}
        onClose={() => setConfirmDeleteDialog({ open: false, listId: '', listName: '' })}
      >
        <DialogTitle>Delete List</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the list "{confirmDeleteDialog.listName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDialog({ open: false, listId: '', listName: '' })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteList} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add Contact to List Dialog */}
      <Dialog open={addContactDialogOpen} onClose={() => setAddContactDialogOpen(false)}>
        <DialogTitle>Add Contacts to List</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="dense"
            label="Search by name or email"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Filter by Tag</InputLabel>
            <Select
              value={filterTag}
              onChange={e => setFilterTag(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {[...new Set(contacts.flatMap(c => c.tags || []))].map(tag => (
                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ maxHeight: 300, overflowY: 'auto', mt: 2 }}>
            {filteredContacts.map(contact => (
              <Box key={contact.id} display="flex" alignItems="center">
                <input
                  type="checkbox"
                  checked={selectedContactIds.includes(contact.id)}
                  onChange={() => handleToggleContact(contact.id)}
                />
                <Typography sx={{ ml: 1 }}>
                  {contact.firstName} {contact.lastName} ({contact.email})
                </Typography>
              </Box>
            ))}
            {filteredContacts.length === 0 && (
              <Typography variant="body2" color="text.secondary">No contacts found.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddContactDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddContactsToList} variant="contained" disabled={selectedContactIds.length === 0}>Add Selected</Button>
        </DialogActions>
      </Dialog>
      {/* Smart List Dialog */}
      <Dialog open={smartListDialogOpen} onClose={() => setSmartListDialogOpen(false)}>
        <DialogTitle>Create Smart List</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Smart List Name"
            fullWidth
            value={smartListName}
            onChange={e => setSmartListName(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Filter by Tag</InputLabel>
            <Select
              value={smartListTag}
              onChange={e => setSmartListTag(e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {[...new Set(contacts.flatMap(c => c.tags || []))].map(tag => (
                <MenuItem key={tag} value={tag}>{tag}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Minimum Transactions"
            type="number"
            fullWidth
            value={smartListMinTransactions}
            onChange={e => setSmartListMinTransactions(e.target.value)}
          />
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Preview: {smartListPreview.length} contacts match these rules.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSmartListDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              try {
                await createSmartList(smartListName, {
                  tag: smartListTag || undefined,
                  minTransactions: smartListMinTransactions ? parseInt(smartListMinTransactions) : undefined,
                });
                setSmartListDialogOpen(false);
                setSmartListName('');
                setSmartListTag('');
                setSmartListMinTransactions('');
                setSuccess('Smart list created!');
              } catch (err) {
                setError('Failed to create smart list');
              }
            }}
            variant="contained"
            disabled={!smartListName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListManager; 